import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Route from '@ember/routing/route';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';
import type DesignerController from 'webapp/controllers/designer';
import type ReportsEditorReportController from 'webapp/controllers/reports/editor/report';
import type ReportsEditorReportRoute from 'webapp/routes/reports/editor/report';

import type Board from 'webapp/models-custom/board';
import type ReportSlide from 'webapp/models-custom/report-slide';

interface Args {
	model: ReportSlide;
	onChange: () => void;
	onDuplicate: () => void;
	onRemove: () => void;
}

export default class ReportsEditorSlideSettingsTypeBoardComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;

	get board(): Board | null {
		return this.args.model.board;
	}

	get isEmpty(): boolean {
		return !this.board;
	}

	get isBoardEditDisabled(): boolean {
		return !!this.board && !this.board.canCurrentUserEdit;
	}

	@action
	editBoard(): void {
		if (this.isBoardEditDisabled) {
			return;
		}

		const board = this.board;
		if (board) {
			const slideId = this.args.model.id!;
			this.navigationService
				.transitionToRoute('designer.board', board.model)
				.then((designerBoardIndexRoute) => {
					const designerController = (designerBoardIndexRoute as Route).controllerFor(
							'designer',
						) as DesignerController,
						enteringTransition = designerController.enteringTransition;
					if (enteringTransition) {
						enteringTransition.data.afterReturn = (
							reportsEditorReportRoute: ReportsEditorReportRoute,
						) => {
							const reportsEditorReportController =
								reportsEditorReportRoute.controller as ReportsEditorReportController;
							if (reportsEditorReportController) {
								schedule('afterRender', () => {
									reportsEditorReportController.changeSlide(slideId);
								});
							}
						};
					}
				});
		}
	}
	<template>
		<div class="actions">

			{{#unless this.isEmpty}}

				<div class="action action-edit">
					<a
						role="button"
						class="button theme-blue-outlined
							{{if this.isBoardEditDisabled 'disabled'}}"
						{{on "click" this.editBoard}}
					>
						<CommonUiIconComponent @name="Pencil" @size="sm" />
						Edit dashboard
						{{#if this.isBoardEditDisabled}}
							<ToolTipComponent
								@text="You don't have permissions to edit this dashboard."
							/>
						{{/if}}
					</a>
				</div>

				<div class="action action-duplicate">
					<a {{on "click" @onDuplicate}}>
						<CommonUiIconComponent @name="Copy" @size="sm" />
						Duplicate
					</a>
				</div>

			{{/unless}}

			<div class="action action-delete">
				<a {{on "click" @onRemove}}>
					<CommonUiIconComponent @name="Trash" @size="sm" />
					Delete slide
				</a>
			</div>

		</div>
	</template>
}
